import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { bestPriceService } from '@/api/services/BestPriceService';
import { orderService } from '@/api/services/OrderService';
import { combineBeneficialOffers } from '@/lib/bestOffer';
import { headlineQuality } from '@/lib/format';
import { useAuth } from '@/stores/authContext';
import type { MyBestOffer } from '@/types/bestPrice';
import type { GoodsProperty } from '@/types/buy';

export const BEST_OFFERS_QUERY_KEY = ['bestOffers'] as const;
export const SALE_BEST_OFFERS_QUERY_KEY = ['bestOffers', 'sale'] as const;

// Сколько лучших встречных тянем на каждую сторону. Берём с запасом (виджет пагинирует на клиенте);
// важно тянуть стороны ОТДЕЛЬНО — иначе side='ALL' с общим лимитом «вымывает» закупку, когда продаж
// с бо́льшей выгодой много (top-N оказывается целиком продажным).
const BEST_OFFERS_PER_SIDE = 50;

// Лучшие встречные предложения по моим позициям (виджет дашборда). Тянем ОБЕ стороны (SELL+BUY)
// отдельными запросами и сводим через combineBeneficialOffers: оставляем только ВЫГОДНЫЕ (advantage>=0)
// и сортируем по выгоде. Бэкенд отдаёт top-N по убыванию выгоды, поэтому выгодные всегда в начале и
// фильтр ничего не теряет. Мягкий: ошибка/пусто → секция не показывается.
export function useBestOffers(): { data: MyBestOffer[]; isLoading: boolean } {
  const { isAuthenticated } = useAuth();
  const results = useQueries({
    queries: (['SELL', 'BUY'] as const).map((side) => ({
      queryKey: [...BEST_OFFERS_QUERY_KEY, side.toLowerCase()] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        bestPriceService.getMyBestOffers(side, BEST_OFFERS_PER_SIDE, signal),
      enabled: isAuthenticated,
      staleTime: 60_000,
    })),
  });

  const sellData = results[0]?.data;
  const buyData = results[1]?.data;
  const isLoading = results.some((result) => result.isLoading);

  const data = useMemo(
    () => combineBeneficialOffers(sellData ?? [], buyData ?? []),
    [sellData, buyData],
  );

  return { data, isLoading };
}

/** Ключ карты качества: культура+набор свойств (по нему матчим оффер к его свойствам). */
export function offerQualityKey(goodsType: string | undefined, propertySetId: number | undefined): string {
  return `${goodsType ?? ''}:${propertySetId ?? ''}`;
}

// Главный параметр качества (протеин/клейковина/… или первое свойство) по каждому офферу виджета.
// Резолвим propertySetId → свойства через словарь /order/goods_propersties (кэш по культуре) на клиенте:
// best-price сам свойства не отдаёт (только id). Возвращаем Map «культура:propsSetId» → GoodsProperty.
// Мягкий: словарь не загрузился/нет свойств → офферов в карте нет, чип качества просто не рисуется.
export function useOffersQuality(offers: readonly MyBestOffer[]): ReadonlyMap<string, GoodsProperty> {
  const { isAuthenticated } = useAuth();
  // Различные культуры офферов, у которых есть набор свойств (иначе резолвить нечего).
  const goodsTypes = useMemo(
    () => [...new Set(offers.filter((o) => o.goodsType && o.propertySetId != null).map((o) => o.goodsType as string))],
    [offers],
  );
  const results = useQueries({
    queries: goodsTypes.map((goodsType) => ({
      queryKey: ['goodsPropertySets', goodsType] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) => orderService.getGoodsPropertySets(goodsType, signal),
      enabled: isAuthenticated,
      staleTime: 5 * 60_000,
    })),
  });
  return useMemo(() => {
    const map = new Map<string, GoodsProperty>();
    goodsTypes.forEach((goodsType, i) => {
      for (const set of results[i]?.data ?? []) {
        const primary = headlineQuality(set.properties);
        if (primary) {
          map.set(offerQualityKey(goodsType, set.propsSetId), primary);
        }
      }
    });
    return map;
    // results — новый массив каждый рендер; пересчёт карты дёшев и корректен при подгрузке словарей.
  }, [goodsTypes, results]);
}

// Лучшая встречная цена по КАЖДОМУ моему офферу на продажу → Map myOrderId → MyBestOffer, чтобы
// показать «Лучшая цена» прямо на карточке лота. Мягкий: нет спроса по офферу → его нет в карте.
export function useSaleBestOffersMap(): ReadonlyMap<number, MyBestOffer> {
  const { isAuthenticated } = useAuth();
  const { data } = useQuery({
    queryKey: SALE_BEST_OFFERS_QUERY_KEY,
    queryFn: ({ signal }) => bestPriceService.getMyBestOffers('SELL', 100, signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  return useMemo(() => new Map((data ?? []).map((offer) => [offer.myOrderId, offer])), [data]);
}

// Ориентир «по какой цене можно продать» в форме создания оффера: рекомендованные FCA-цены по типам
// оплаты для базиса+культуры+качества. Запрос идёт, когда заполнены все три (цену/год/объём не ждём).
// Мягкий: пустота/ошибка → панель покажет «спроса нет» / скроется (вызывающий не блокирует форму).
// В ключе НЕТ оборудования: сервер отдаёт цену при всём включённом оборудовании + дистанцию, а надбавки
// за отключённое оборудование вычитает клиент (мгновенно, без повторного запроса).
export function useSellPriceCandidates(params: {
  readonly address?: string;
  readonly goodsType?: string | null;
  readonly propsSetId?: string | number | null;
}) {
  const { isAuthenticated } = useAuth();
  const propertySetId = params.propsSetId != null && params.propsSetId !== '' ? Number(params.propsSetId) : null;
  const enabled = isAuthenticated && !!params.address && !!params.goodsType && propertySetId != null;
  return useQuery({
    queryKey: ['sellPriceCandidates', params.address, params.goodsType, propertySetId] as const,
    queryFn: ({ signal }) =>
      bestPriceService.getSellPriceCandidates(
        { address: params.address!, goodsType: params.goodsType!, propertySetId: propertySetId! },
        signal,
      ),
    enabled,
    staleTime: 60_000,
  });
}

// Ставки надбавок к тарифу перевозки (₽/т·км) + порог дистанции — для подписей у переключателей
// оборудования и клиентского пересчёта ориентира. Меняются редко (конфигурация) → долгий кэш.
export function useTransportSurcharges() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['transportSurcharges'] as const,
    queryFn: ({ signal }) => bestPriceService.getTransportSurcharges(signal),
    enabled: isAuthenticated,
    staleTime: 30 * 60_000,
  });
}
